import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import api from '../services/api';
import { PERMISOS } from '../utils/permissions';
import { fechaHoy } from '../utils/dateUtils';
import '../styles/RegistroInformacion.css';
import { LuFileText, LuFileDigit,LuCalendarDays,LuSave ,LuCalendarClock,LuRectangleEllipsis ,LuChurch,LuPaperclip ,LuFileType,LuCircleDollarSign, LuUsers,LuPin } from "react-icons/lu";

const RegistroInformacion = () => {
  const { usuario } = useAuth();
  const toast = useToast();
  
  const [formData, setFormData] = useState({
    TipoRegistro: 'Ingreso',
    Fecha: fechaHoy(),
    Hora: new Date().toTimeString().slice(0, 5),
    Local: usuario?.Local || '',
    centroCosto: usuario?.CentroCosto || '',
    grupo: usuario?.GrupoId || '',
    Motivo: '',
    TipoDoc: 'SD',
    NumDoc: 0,
    Monto: '',
    Glosa: ''
  });

  const [catalogos, setCatalogos] = useState({
    locales: [],
    grupos: [],
    motivos: [],
    tiposDoc: []
  });

  const [loading, setLoading] = useState(false);
  const [imagenes, setImagenes] = useState([]);

  const camposDeshabilitados = {
    local: usuario.Permiso < PERMISOS.ZONA,
    grupo: usuario.Permiso < PERMISOS.ADMINISTRADOR
  };

  useEffect(() => {
    const cargarCatalogos = async () => {
      try {
        const response = await api.get('/catalogos');
        if (response.data.success) {
          let locales = response.data.data.locales;
          let grupos = response.data.data.grupos;

          if (usuario.Permiso < PERMISOS.ZONA) {
            locales = locales.filter(local => local.CodLocal === usuario.Local);
          }

          if (usuario.Permiso < PERMISOS.ADMINISTRADOR) {
            grupos = grupos.filter(grupo => grupo.id === usuario.GrupoId);
          }

          setCatalogos({
            locales,
            grupos,
            motivos: response.data.data.motivos,
            tiposDoc: response.data.data.tiposDoc
          });
        }
      } catch (error) {
        console.error('Error al cargar catálogos:', error);
        toast.error('Error al cargar los catálogos');
      }
    };

    cargarCatalogos();
  }, [usuario.Permiso, usuario.Local, usuario.GrupoId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleTipoRegistroChange = (tipo) => {
    setFormData({
      ...formData,
      TipoRegistro: tipo
    });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setImagenes(files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Validaciones con alerts
    if (!formData.Motivo) {
      toast.warning('Debe seleccionar un motivo');
      return;
    }

    if (!formData.Monto || parseFloat(formData.Monto) <= 0) {
      toast.warning('Debe ingresar un monto válido');
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();
      
      Object.keys(formData).forEach(key => {
        if (key === 'grupo') {
          formDataToSend.append('CentroCosto', formData[key]);
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });

      imagenes.forEach(file => {
        formDataToSend.append('imagenes', file);
      });

      const response = await api.post('/documentos', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        toast.success('Documento registrado exitosamente');
        
        // Limpiar formulario
        setFormData({
          TipoRegistro: 'Ingreso',
          Fecha: fechaHoy(),
          Hora: new Date().toTimeString().slice(0, 5),
          Local: usuario?.Local || '',
          grupo: usuario?.GrupoId || '',
          Motivo: '',
          TipoDoc: 'SD',
          NumDoc: 0,
          Monto: '',
          Glosa: ''
        });
        setImagenes([]);
        
        const fileInput = document.getElementById('imagenes');
        if (fileInput) fileInput.value = '';
      }
    } catch (error) {
      console.error('Error al registrar:', error);
      toast.error(error.response?.data?.message || 'Error al registrar documento');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="registro-container">
      <div className="registro-header">
        <h2>Registro de Información</h2>
        {(camposDeshabilitados.local || camposDeshabilitados.grupo) && (
          <p className="restriccion-info">
            ℹ️ Registrando documentos para: {usuario?.localInfo?.Nombre} - {usuario?.grupoInfo?.Descripcion || usuario?.grupoInfo?.Nombre}
          </p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="registro-form">
        {/* Tipo de registro */}
        <div className="form-section">
          <label className="form-label">
            <i className="icon"><LuFileText /></i> Tipo de registro
          </label>
          <div className="tipo-registro-buttons">
            <button
              type="button"
              className={`tipo-btn ${formData.TipoRegistro === 'Ingreso' ? 'active ingreso' : ''}`}
              onClick={() => handleTipoRegistroChange('Ingreso')}
            >
              <span className="radio-icon">{formData.TipoRegistro === 'Ingreso' ? '⦿' : '○'}</span>
              Ingreso
            </button>
            <button
              type="button"
              className={`tipo-btn ${formData.TipoRegistro === 'Gasto' ? 'active gasto' : ''}`}
              onClick={() => handleTipoRegistroChange('Gasto')}
            >
              <span className="radio-icon">{formData.TipoRegistro === 'Gasto' ? '⦿' : '○'}</span>
              Gasto
            </button>
          </div>
        </div>

        {/* Fecha y Hora */}
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="Fecha" className="form-label">
              <i className="icon"><LuCalendarDays /></i> Fecha
            </label>
            <input
              type="date"
              id="Fecha"
              name="Fecha"
              value={formData.Fecha}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="Hora" className="form-label">
              <i className="icon"><LuCalendarClock /></i> Hora
            </label>
            <input
              type="time"
              id="Hora"
              name="Hora"
              value={formData.Hora}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>
        </div>

        {/* Local y Grupo */}
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="Local" className="form-label">
              <i className="icon"><LuChurch /></i> Iglesia
              {camposDeshabilitados.local && <span className="badge-bloqueado">🔒</span>}
            </label>
            <select
              id="Local"
              name="Local"
              value={formData.Local}
              onChange={handleChange}
              required
              disabled={loading || camposDeshabilitados.local}
            >
              <option value="">Seleccione un local</option>
              {catalogos.locales.map(local => (
                <option key={local.id} value={local.CodLocal}>
                  {local.Nombre}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="grupo" className="form-label">
              <i className="icon"><LuUsers/></i> Grupo
              {camposDeshabilitados.grupo && <span className="badge-bloqueado">🔒</span>}
            </label>
            <select
              id="grupo"
              name="grupo"
              value={formData.grupo}
              onChange={handleChange}
              required
              disabled={loading || camposDeshabilitados.grupo}
            >
              <option value="">Seleccione un grupo</option>
              {catalogos.grupos.map(grupo => (
                <option key={grupo.id} value={grupo.id}>
                  {grupo.Descripcion || grupo.Nombre}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Motivo */}
        <div className="form-group">
          <label htmlFor="Motivo" className="form-label">
            <i className="icon"><LuPin /></i> Motivo
          </label>
          <select
            id="Motivo"
            name="Motivo"
            value={formData.Motivo}
            onChange={handleChange}
            required
            disabled={loading}
          >
            <option value="">Seleccione un motivo</option>
            {catalogos.motivos.map(motivo => (
              <option key={motivo.Id} value={motivo.CodMotivo}>
                {motivo.NombreMotivo}
              </option>
            ))}
          </select>
        </div>

        {/* Tipo de documento */}
        <div className="form-group">
          <label htmlFor="TipoDoc" className="form-label">
            <i className="icon"><LuFileType/></i> Tipo de documento
          </label>
          <select
            id="TipoDoc"
            name="TipoDoc"
            value={formData.TipoDoc}
            onChange={handleChange}
            disabled={loading}
          >
            {catalogos.tiposDoc.map(tipo => (
              <option key={tipo.id} value={tipo.CodDoc}>
                {tipo.Nombre}
              </option>
            ))}
          </select>
        </div>

        {/* Número Documento */}
        <div className="form-group">
          <label htmlFor="NumDoc" className="form-label">
            <i className="icon"><LuFileDigit /></i> Número Documento
          </label>
          <input
            type="number"
            id="NumDoc"
            name="NumDoc"
            value={formData.NumDoc}
            onChange={handleChange}
            min="0"
            disabled={loading}
          />
        </div>

        {/* Monto */}
        <div className="form-group">
          <label htmlFor="Monto" className="form-label">
            <i className="icon"><LuCircleDollarSign/></i> Monto
          </label>
          <input
            type="number"
            id="Monto"
            name="Monto"
            value={formData.Monto}
            onChange={handleChange}
            placeholder="0"
            min="0"
            step="0.01"
            required
            disabled={loading}
          />
        </div>

        {/* Adjuntar Imágenes */}
        <div className="form-group">
          <label htmlFor="imagenes" className="form-label">
            <i className="icon"><LuPaperclip /></i> Adjuntar Imágenes o PDF
          </label>
          <input
            type="file"
            id="imagenes"
            name="imagenes"
            onChange={handleFileChange}
            multiple
            accept="image/*,.pdf"
            disabled={loading}
          />
          {imagenes.length > 0 && (
            <small className="file-count">{imagenes.length} archivo(s) seleccionado(s)</small>
          )}
        </div>

        {/* Glosa */}
        <div className="form-group">
          <label htmlFor="Glosa" className="form-label">
            <i className="icon"><LuRectangleEllipsis /></i> Glosa
          </label>
          <textarea
            id="Glosa"
            name="Glosa"
            value={formData.Glosa}
            onChange={handleChange}
            placeholder="Glosa"
            rows="4"
            maxLength="500"
            disabled={loading}
          />
        </div>

        {/* Botón Guardar */}
        <div className="form-actions">
          <button type="submit" className="btn-guardar" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner-small"></span>
                Guardando...
              </>
            ) : (
              <>
                <i className="icon"><LuSave /></i>
                Guardar
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RegistroInformacion;